import { useEffect, useMemo, useState } from "react";
import { usePermissions } from "../../Auth/PermissionsContext";
import { resolveStorageUrl } from "../../Helper/storage";
import { getUserDirectory, type DirectoryEntry } from "./accountClient";

const norm = (s?: string | null): string => (s ?? "").trim().toLowerCase();

/**
 * Fetches the slim user directory ONCE (only when the caller holds feature:profile:read) and returns a
 * resolver that maps an actor's display name OR email to their profile id : so name-only surfaces (audit
 * logs, the recent-changes feed) can link to /account/{id}. Returns null when unknown or unauthorized,
 * in which case the caller renders plain text instead of a link. Never throws.
 */
export function useUserDirectory(): {
	resolveId: (nameOrEmail?: string | null) => string | null;
	/** Maps an actor's display name OR email to their avatar URL : the avatar uploaded in the admin
	 *  (avatarKey → storage URL) when they have one, else the Auth0/Google photo, else null. */
	resolvePicture: (nameOrEmail?: string | null) => string | null;
	/** Badges de rôles maximaux d'un acteur (nom OU email) ; [] quand inconnu/non autorisé. */
	resolveBadges: (nameOrEmail?: string | null) => NonNullable<DirectoryEntry["badges"]>;
	/** L'annuaire complet (pour les surfaces qui listent l'équipe, ex. carte Équipe). */
	entries: DirectoryEntry[];
	allowed: boolean;
} {
	const { can } = usePermissions();
	const allowed = can("feature:profile:read");
	const [entries, setEntries] = useState<DirectoryEntry[]>([]);

	useEffect(() => {
		let cancelled = false;
		if (!allowed) {
			setEntries([]);
			return;
		}
		getUserDirectory()
			.then((d) => {
				if (!cancelled) setEntries(d);
			})
			.catch(() => {
				if (!cancelled) setEntries([]);
			});
		return () => {
			cancelled = true;
		};
	}, [allowed]);

	const { resolveId, resolvePicture, resolveBadges } = useMemo(() => {
		const byName = new Map<string, string>();
		const byEmail = new Map<string, string>();
		const picByName = new Map<string, string>();
		const picByEmail = new Map<string, string>();
		const badgesByName = new Map<string, NonNullable<DirectoryEntry["badges"]>>();
		const badgesByEmail = new Map<string, NonNullable<DirectoryEntry["badges"]>>();
		for (const e of entries) {
			if (e.id) {
				if (e.name) byName.set(norm(e.name), e.id);
				if (e.email) byEmail.set(norm(e.email), e.id);
			}
			// The uploaded avatar (avatarKey) wins over the Auth0/Google photo : same priority as UserAvatar.
			const pic = (e.avatarKey ? resolveStorageUrl(e.avatarKey) : null) ?? e.picture ?? null;
			if (pic) {
				if (e.name) picByName.set(norm(e.name), pic);
				if (e.email) picByEmail.set(norm(e.email), pic);
			}
			if (e.badges?.length) {
				if (e.name) badgesByName.set(norm(e.name), e.badges);
				if (e.email) badgesByEmail.set(norm(e.email), e.badges);
			}
		}
		return {
			resolveId: (nameOrEmail?: string | null): string | null => {
				const k = norm(nameOrEmail);
				if (!k) return null;
				return byName.get(k) ?? byEmail.get(k) ?? null;
			},
			resolvePicture: (nameOrEmail?: string | null): string | null => {
				const k = norm(nameOrEmail);
				if (!k) return null;
				return picByName.get(k) ?? picByEmail.get(k) ?? null;
			},
			resolveBadges: (nameOrEmail?: string | null): NonNullable<DirectoryEntry["badges"]> => {
				const k = norm(nameOrEmail);
				if (!k) return [];
				return badgesByName.get(k) ?? badgesByEmail.get(k) ?? [];
			}
		};
	}, [entries]);

	return { resolveId, resolvePicture, resolveBadges, entries, allowed };
}
